import { useQueryParams, withDefault, JsonParam } from 'use-query-params'
import { useNavigate } from 'react-router-dom'

import FilterSet from '@/gf/modules/FilterSet'
import usePage from '@/gf/hooks/usePage'
import useFilters from '@/gf/hooks/useFilters'
import Address from '@/gf/modules/Address'
import useSession from '../hooks/useSession'
import useSearchCustomers from '@/gf/hooks/useSearchCustomers'
import fields from '../data/customerFilters/fields'

import Page from '@/gf/components/Page'
import Spinner from '@/gf/components/Spinner'
import { Table, Thead, Tr, Th, Tbody, Td } from '@/gf/components/Table'
import Pagination from '@/gf/components/Pagination'
import Layout from '@/gf/components/Layout'
import Filters from '@/gf/components/Filters'
import Frame from '../components/Frame'
import Menu from './Customers/Menu'

import { Filter } from '../../types'

const breadcrumbs = {
  copy: 'Back to Dashboard',
  crumbs: [{ name: 'Customers', href: '/customers' }],
}

const cols = 4

const useQueryParamsOpts = {
  filters: withDefault<Filter[], Filter[]>(JsonParam, []),
}

const Customers = () => {
  const navigate = useNavigate()
  const { store } = useSession()
  const [page, setPage] = usePage()
  const [query, updateQuery] = useQueryParams(useQueryParamsOpts)
  const [stagedFilters, filtersApi] = useFilters(query.filters)

  const filter = (() => {
    const extra = store.onboardedSuppDashDatetime
      ? [['greater_than', 'inserted_at', store.onboardedSuppDashDatetime]]
      : []

    return JSON.stringify([
      ['equals', 'store_id', store.id],
      ...extra,
      ...FilterSet.toApiFilters(query.filters),
    ])
  })()

  const searchResults = useSearchCustomers({ variables: { page, filter } })
  const customersSearch = searchResults.data?.customersSearch

  const updateFilters = (newFilters: Filter[]) => {
    setPage(1)
    updateQuery({ filters: newFilters })
  }

  return (
    <Frame breadcrumbs={breadcrumbs}>
      <Page title="Customers">
        <Menu />
        <Layout>
          <Layout.Section type="table">
            <Filters
              filters={query.filters}
              stagedFilters={stagedFilters}
              fields={fields}
              filtersApi={filtersApi}
              onApply={() => updateFilters(stagedFilters)}
            />
            <Table>
              <Thead>
                <Tr>
                  <Th>Name</Th>
                  <Th>Account</Th>
                  <Th>Location</Th>
                  <Th>Orders</Th>
                </Tr>
              </Thead>
              <Tbody>
                {customersSearch?.customers.map((customer) => (
                  <Tr
                    key={customer.id}
                    onClick={() => {
                      navigate(`/customers/${customer.id}`)
                    }}
                  >
                    <Td>{customer.name}</Td>
                    <Td>{customer.account.name}</Td>
                    <Td>{Address.formatCityStateString(customer.shippingAddress)}</Td>
                    <Td>{customer.ordersCount}</Td>
                  </Tr>
                ))}

                {!customersSearch && (
                  <Tr>
                    <Td colSpan={cols}>
                      <Spinner />
                    </Td>
                  </Tr>
                )}

                {customersSearch?.customers.length === 0 && (
                  <Tr>
                    <Td colSpan={cols}>No customers found.</Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </Layout.Section>
          <Layout.Section type="full">
            <Pagination pagination={customersSearch?.pagination} page={page} updatePage={setPage} />
          </Layout.Section>
        </Layout>
      </Page>
    </Frame>
  )
}

export default Customers
